import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const TimerContext = createContext(null);

export const TimerProvider = ({ children }) => {
  const [active, setActive] = useState(null); // {id, job_id, job_title, start_time}
  const [elapsed, setElapsed] = useState(0); // seconds
  const tick = useRef(null);

  const fetchActive = useCallback(async () => {
    try {
      const res = await api.get("/timer/active");
      setActive(res.data || null);
    } catch {
      setActive(null);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("osi_token")) fetchActive();
  }, [fetchActive]);

  useEffect(() => {
    if (active?.start_time) {
      const update = () => {
        const secs = Math.max(0, Math.floor((Date.now() - new Date(active.start_time).getTime()) / 1000));
        setElapsed(secs);
      };
      update();
      tick.current = setInterval(update, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(tick.current);
  }, [active]);

  const clockIn = async (jobId) => {
    try {
      const res = await api.post("/timer/clock-in", { job_id: jobId });
      setActive(res.data);
      toast.success(`Clocked in: ${res.data.job_title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clock in");
    }
  };

  const clockOut = async () => {
    try {
      const res = await api.post("/timer/clock-out", { notes: "" });
      setActive(null);
      toast.success(`Clocked out — ${res.data.hours} hrs logged`);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clock out");
    }
  };

  const fmtElapsed = () => {
    const h = String(Math.floor(elapsed / 3600)).padStart(2, "0");
    const m = String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0");
    const s = String(elapsed % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  return (
    <TimerContext.Provider value={{ active, elapsed, fmtElapsed, clockIn, clockOut, fetchActive }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => useContext(TimerContext);
